import { type CSSProperties, type ReactNode } from 'react'
import { Navigate, NavLink } from 'react-router-dom'
import { useAuthStore } from '@/store/auth'
import { cn } from '@/lib/cn'

interface AccountPageProps {
  title: string
  children: ReactNode
  /**
   * What this page holds, stated as a count: "3 keys · 1 unclaimed". Set in
   * the monospace face, which is this product's voice for an exact quantity —
   * the same one a revealed key and a notification timestamp use.
   */
  meta?: ReactNode
  /** Optional primary control shown beside the heading. */
  action?: ReactNode
}

/**
 * The three account sections are one place, so they are navigable as one.
 * Reaching Media from Loot previously meant going back up to the toolbar.
 */
const SECTIONS = [
  { to: '/my/loot', label: 'Loot' },
  { to: '/my/giveaways', label: 'Giveaways' },
  { to: '/my/media', label: 'Media' },
  { to: '/my/games', label: 'Games' },
] as const

/**
 * Shell for the signed-in account pages (`/my/*`).
 *
 * Each of these was a modal in the Quasar app, which meant they had no address,
 * no back button and no scroll position. Signed-out visitors are sent home
 * rather than shown an empty frame.
 *
 * The header is the connective tissue: the same title block, count line and
 * section switcher on all three, so moving between them changes the contents
 * rather than the furniture.
 */
export function AccountPage({ title, children, meta, action }: AccountPageProps) {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated)

  if (!isAuthenticated) return <Navigate to="/" replace />

  return (
    <div className="account-page">
      <header className="account-header rise">
        <div className="account-heading">
          <h1 className="account-title">{title}</h1>
          {meta ? <div className="account-meta">{meta}</div> : null}
        </div>
        {action ? <div className="account-action">{action}</div> : null}
      </header>

      <nav
        className="account-sections rise"
        aria-label="Account sections"
        style={{ '--d': '60ms' } as CSSProperties}
      >
        {SECTIONS.map((section) => (
          <NavLink
            key={section.to}
            to={section.to}
            className={({ isActive }) => cn('account-section', isActive && 'active')}
          >
            {section.label}
          </NavLink>
        ))}
      </nav>

      <div className="account-body">{children}</div>
    </div>
  )
}
